export type CypherpunkFormatOpts = { forTable?: boolean };

const DISPLAY_DECIMALS = 1;

/** Round to `decimals` places: if the next digit is > 5, round up; otherwise floor. */
export function roundDisplay(value: number, decimals = DISPLAY_DECIMALS): number {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** decimals;
  const scaled = value * factor;
  const sign = scaled < 0 ? -1 : 1;
  const absScaled = Math.abs(scaled);
  const floored = Math.floor(absScaled);
  const nextDigit = Math.floor((absScaled - floored) * 10 + 1e-9);
  const rounded = nextDigit > 5 ? floored + 1 : floored;
  return (sign * rounded) / factor;
}

function fixed(value: number, decimals: number, opts?: CypherpunkFormatOpts): string {
  if (opts?.forTable) return value.toFixed(decimals);
  return roundDisplay(value, decimals).toFixed(decimals);
}

export function formatNumberFixed(value: number, decimals = DISPLAY_DECIMALS): string {
  return roundDisplay(value, decimals).toFixed(decimals);
}

export function formatUsdCompact(
  value: number,
  decimals = DISPLAY_DECIMALS,
  opts?: CypherpunkFormatOpts,
): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${fixed(value / 1_000_000_000, decimals, opts)}B`;
  if (abs >= 1_000_000) return `$${fixed(value / 1_000_000, decimals, opts)}M`;
  if (abs >= 1_000) return `$${fixed(value / 1_000, decimals, opts)}K`;
  return `$${fixed(value, decimals, opts)}`;
}

export function formatUsdMillions(value: number, opts?: CypherpunkFormatOpts): string {
  const d = opts?.forTable ? 2 : DISPLAY_DECIMALS;
  if (value >= 1_000_000) return `$${fixed(value / 1_000_000, d, opts)}M`;
  if (value >= 1_000) return `$${fixed(value / 1_000, d, opts)}K`;
  return `$${fixed(value, d, opts)}`;
}

export function formatZec(value: number, opts?: CypherpunkFormatOpts): string {
  const n = opts?.forTable ? value : roundDisplay(value, DISPLAY_DECIMALS);
  const fractionDigits = opts?.forTable ? 2 : DISPLAY_DECIMALS;
  return n.toLocaleString("en-US", {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });
}

export function formatZecSigned(delta: number, opts?: CypherpunkFormatOpts): string {
  const prefix = delta >= 0 ? "+" : "";
  return `${prefix}${formatZec(delta, opts)}`;
}

export function formatPct(value: number, decimals = DISPLAY_DECIMALS, opts?: CypherpunkFormatOpts): string {
  return `${fixed(value, decimals, opts)}%`;
}

export function formatPctSigned(
  delta: number,
  decimals = DISPLAY_DECIMALS,
  opts?: CypherpunkFormatOpts,
): string {
  const prefix = delta >= 0 ? "+" : "";
  return `${prefix}${formatPct(delta, decimals, opts)}`;
}

export function formatPpSigned(
  delta: number,
  decimals = DISPLAY_DECIMALS,
  opts?: CypherpunkFormatOpts,
): string {
  const prefix = delta >= 0 ? "+" : "";
  return `${prefix}${fixed(delta, decimals, opts)}pp`;
}

export function formatDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatTableDate(iso: string): string {
  return iso.slice(0, 10);
}
